// Local variable/parameter-in-scope discovery for completion and hover,
// walked directly over `@hollow/script`'s own published AST (`Program`,
// `Statement`, `Expression`, ... - all exported from its public index.d.ts:
// the parser output is part of the supported integration surface, the
// checker's internal `Scope`/`checkScope` and its type-inference are not
// (neither is exported - see `@hollow/script`'s `dist/index.d.ts`), so this
// module never reaches for either. That means completion/hover here can
// offer a binding's *declared* type annotation (when the creator wrote one)
// but never the checker's *inferred* one - a real, deliberate limit, not an
// oversight (see this repo's task brief: "if @hollow/script's check()/
// parse() output exposes enough scope information for this without deep
// interpreter surgery").
//
// Visibility is deliberately coarser than the language's real (sequential,
// declaration-order) scoping rule: every binding declared anywhere in an
// enclosing block is offered, not just the ones that textually precede the
// cursor. A false-positive suggestion (a `constant` declared three lines
// below the cursor) is a much smaller cost than a missing one for a
// completion list, and getting real declaration-order visibility right
// across `if`/`match`/`for` branches is exactly the "deep interpreter
// surgery" the brief says is fine to skip.

import type { Block, Expression, Program, Span, Statement, TopLevelItem } from '@hollow/script';
import type { FnDecl, Param, TypeNode } from './astTypes.js';
import { typeNodeToString } from './typeText.js';

export type ScopeBindingKind = 'constant' | 'variable' | 'parameter' | 'function' | 'record' | 'import' | 'capability';

export interface ScopeBinding {
  readonly name: string;
  readonly kind: ScopeBindingKind;
  /** The declared type annotation's own text, when the binding carries one
   * (a `ConstantDecl`/`VariableDecl`/`Param`'s `: Type`, or a `FnDecl`'s
   * full signature) - `undefined` when none was written, never a guess. */
  readonly typeText: string | undefined;
  readonly span: Span;
}

function within(span: Span, offset: number): boolean {
  return offset >= span.start && offset <= span.end;
}

function pushParam(bindings: ScopeBinding[], param: Param): void {
  bindings.push({ name: param.name.name, kind: 'parameter', typeText: typeNodeToString(param.type), span: param.name.span });
}

function fnSignatureText(fn: FnDecl): string {
  const params = fn.parameters.map((p) => `${p.name.name}: ${typeNodeToString(p.type)}`).join(', ');
  const ret: TypeNode | undefined = fn.returnType;
  return `fn(${params})${ret ? `: ${typeNodeToString(ret)}` : ''}`;
}

/** Walks into whichever branch of an expression's subtree could contain
 * `offset`, contributing a `ClosureExpr`'s parameters and body bindings when
 * found. Every other expression kind is a pure tree of subexpressions with
 * no binding forms of its own (HollowScript has no `let`-in-expression
 * form), so this only needs to keep descending, not bind anything itself. */
function collectFromExpression(expr: Expression | undefined, offset: number, bindings: ScopeBinding[]): void {
  if (expr === undefined || !within(expr.span, offset)) return;
  switch (expr.kind) {
    case 'ClosureExpr':
      for (const param of expr.parameters) pushParam(bindings, param);
      collectFromStatements(expr.body.statements, offset, bindings);
      return;
    case 'StringLiteral':
      for (const part of expr.parts) {
        if (part.kind === 'StringInterpolation') collectFromExpression(part.expression, offset, bindings);
      }
      return;
    case 'ListLiteral':
      for (const element of expr.elements) collectFromExpression(element, offset, bindings);
      return;
    case 'RecordLiteral':
      for (const field of expr.fields) collectFromExpression(field.value, offset, bindings);
      return;
    case 'UnaryExpr':
      collectFromExpression(expr.operand, offset, bindings);
      return;
    case 'BinaryExpr':
      collectFromExpression(expr.left, offset, bindings);
      collectFromExpression(expr.right, offset, bindings);
      return;
    case 'CallExpr':
      collectFromExpression(expr.callee, offset, bindings);
      for (const arg of expr.arguments) collectFromExpression(arg, offset, bindings);
      return;
    case 'IndexExpr':
      collectFromExpression(expr.target, offset, bindings);
      collectFromExpression(expr.index, offset, bindings);
      return;
    case 'MemberExpr':
      collectFromExpression(expr.target, offset, bindings);
      return;
    default:
      // IntLiteral / FloatLiteral / BooleanLiteral / NoneLiteral / FilterLiteral
      // / Identifier / ErrorExpr: nothing nested to descend into.
      return;
  }
}

function collectFromBlockIfWithin(block: Block, offset: number, bindings: ScopeBinding[]): void {
  if (within(block.span, offset)) collectFromStatements(block.statements, offset, bindings);
}

function collectFromStatements(statements: readonly Statement[], offset: number, bindings: ScopeBinding[]): void {
  for (const stmt of statements) {
    switch (stmt.kind) {
      case 'ConstantDecl':
        bindings.push({ name: stmt.name.name, kind: 'constant', typeText: stmt.type ? typeNodeToString(stmt.type) : undefined, span: stmt.name.span });
        collectFromExpression(stmt.value, offset, bindings);
        break;
      case 'VariableDecl':
        bindings.push({ name: stmt.name.name, kind: 'variable', typeText: stmt.type ? typeNodeToString(stmt.type) : undefined, span: stmt.name.span });
        collectFromExpression(stmt.value, offset, bindings);
        break;
      case 'AssignmentStmt':
        collectFromExpression(stmt.value, offset, bindings);
        break;
      case 'CallStmt':
        collectFromExpression(stmt.call, offset, bindings);
        break;
      case 'ReturnStmt':
        collectFromExpression(stmt.value, offset, bindings);
        break;
      case 'IfStmt':
        collectFromExpression(stmt.condition, offset, bindings);
        collectFromBlockIfWithin(stmt.then, offset, bindings);
        if (stmt.else) {
          if (stmt.else.kind === 'Block') collectFromBlockIfWithin(stmt.else, offset, bindings);
          else collectFromStatements([stmt.else], offset, bindings);
        }
        break;
      case 'MatchStmt':
        collectFromExpression(stmt.scrutinee, offset, bindings);
        for (const branch of stmt.branches) collectFromBlockIfWithin(branch.body, offset, bindings);
        collectFromBlockIfWithin(stmt.elseBranch, offset, bindings);
        break;
      case 'ForStmt':
        collectFromExpression(stmt.iterable, offset, bindings);
        if (within(stmt.body.span, offset)) {
          bindings.push({ name: stmt.variable.name, kind: 'parameter', typeText: undefined, span: stmt.variable.span });
          collectFromStatements(stmt.body.statements, offset, bindings);
        }
        break;
      case 'WhileStmt':
        collectFromExpression(stmt.condition, offset, bindings);
        collectFromBlockIfWithin(stmt.body, offset, bindings);
        break;
      case 'BreakStmt':
      case 'ContinueStmt':
        break;
    }
  }
}

/** Every binding visible from `offset` into `program`: `uses`/`import`
 * names (always in scope, whole-program), every top-level `fn`/`record`
 * (whole-program - HollowScript has no forward-declaration ordering issue
 * for these, they're hoisted the same way a module's top-level always is),
 * and, once `offset` is inside a function/closure body or a nested block,
 * that scope's own parameters and declarations too. Never throws: built
 * entirely from a `parse()` result, which itself never throws on malformed
 * input (spec.md's own gate 2 - the same guarantee `checkHollowScript`
 * leans on in Cascade's `diagnostics.ts`). */
export function collectScopeAt(program: Program, offset: number): readonly ScopeBinding[] {
  const bindings: ScopeBinding[] = [];
  for (const decl of program.uses) {
    for (const name of decl.names) bindings.push({ name: name.name, kind: 'capability', typeText: undefined, span: name.span });
  }
  for (const decl of program.imports) {
    for (const name of decl.names) bindings.push({ name: name.name, kind: 'import', typeText: undefined, span: name.span });
  }

  const topLevelStatements: Statement[] = [];

  function visitFnDecl(fn: FnDecl): void {
    bindings.push({ name: fn.name.name, kind: 'function', typeText: fnSignatureText(fn), span: fn.name.span });
    if (within(fn.span, offset)) {
      for (const param of fn.parameters) pushParam(bindings, param);
      collectFromStatements(fn.body.statements, offset, bindings);
    }
  }

  function visitTopLevel(item: TopLevelItem): void {
    switch (item.kind) {
      case 'ExportedDecl': {
        const decl = item.declaration;
        if (decl.kind === 'FnDecl') visitFnDecl(decl);
        else if (decl.kind === 'RecordDecl') bindings.push({ name: decl.name.name, kind: 'record', typeText: undefined, span: decl.name.span });
        else topLevelStatements.push(decl); // ConstantDecl
        return;
      }
      case 'FnDecl':
        visitFnDecl(item);
        return;
      case 'RecordDecl':
        bindings.push({ name: item.name.name, kind: 'record', typeText: undefined, span: item.name.span });
        return;
      default:
        topLevelStatements.push(item); // a bare Statement
    }
  }

  for (const item of program.topLevel) visitTopLevel(item);
  collectFromStatements(topLevelStatements, offset, bindings);
  return bindings;
}
